import React, { useState } from "react";
import { createPortal } from "react-dom";
import { Box, Button, Chip, IconButton, Stack } from "@mui/material";
import { ArrowBackRounded, DownloadRounded } from "@mui/icons-material";
import { useCurrentEditor } from "@tiptap/react";
import { useSnackbar } from "notistack";
import LegalbirdTipTapEditorProvider from "../LbEditor/LegalbirdTipTapEditorProvider";
import LegalbirdTipTapEditor from "../LbEditor/LegalbirdTipTapEditor";
import ButtonLoading from "../Button/ButtonLoading";
import { useCase } from "../../provider/CaseProvider";
import { useDrawer } from "../../provider/DrawerProvider";
import { previewCaseFile } from "../../services/Files/fileService";
import { DocumentExecutionStepPayload } from "../../types/AIChat/DocumentExecutionStepPayload";
import useDraftAutosave from "../../hooks/useDraftAutosave";
import DraftAutosaveIndicator from "../GlobalDrawer/DraftAutosaveIndicator";
import { buildAddress } from "../Case/CasePdfGenerator/casePdfGeneratorService";
import documentEditorPanelStyles from "./documentEditorPanelStyles";

type DocumentEditorPanelProps = {
  // This payload provides the initial values when the drawer creates a new editor instance.
  payload: DocumentExecutionStepPayload;
  // This callback saves the edited document back to its execution step.
  overwrittenHandleSave?: Function;
};

export default function DocumentEditorPanel({ payload, overwrittenHandleSave }: DocumentEditorPanelProps) {
  return (
    <LegalbirdTipTapEditorProvider initialContent={payload.bodyHtml || ""} placeholder={"Entwurf"}>
      <DocumentEditorPanelContent payload={payload} overwrittenHandleSave={overwrittenHandleSave} />
    </LegalbirdTipTapEditorProvider>
  );
}

function DocumentEditorPanelContent({ payload, overwrittenHandleSave }: DocumentEditorPanelProps) {
  const [previewSrc, setPreviewSrc] = useState("");
  const [previewIsLoading, setPreviewIsLoading] = useState(false);
  const previewMode = !!previewSrc;

  const { product } = useCase();
  const { drawerHeaderNode } = useDrawer();
  const { editor } = useCurrentEditor();
  const { enqueueSnackbar } = useSnackbar();

  const { saveState } = useDraftAutosave({
    enabled: !!overwrittenHandleSave,
    persist: async () => {
      if (!editor || editor.isDestroyed) {
        return;
      }
      await overwrittenHandleSave?.({ ...payload, bodyHtml: editor.getHTML(), lastEditedBy: "user" });
    },
    editor,
  });

  if (!product) {
    return null;
  }

  const openPreview = async () => {
    if (!editor) {
      return;
    }
    setPreviewIsLoading(true);
    try {
      const src = await previewCaseFile({
        product,
        documentType: "pdf",
        documentIdentifier: "customPdf",
        params: {
          content: editor.getHTML(),
          filename: payload.filename,
          recipientType: payload.recipientType,
          address: buildAddress(payload, product),
          externalReference: payload.externalReference ?? null,
          caseReferenceNumber: payload.caseReferenceNumber ?? null,
          insurancePolicyNumber: payload.insurancePolicyNumber ?? null,
          senderMedium: null,
          externalLawyer: null,
        },
      });
      setPreviewSrc(src);
    } catch (error) {
      enqueueSnackbar("PDF-Vorschau konnte nicht erstellt werden.", {
        variant: "error",
        isNonInteractive: true,
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
      });
    } finally {
      setPreviewIsLoading(false);
    }
  };

  return (
    <Stack sx={{ width: "100%" }}>
      {drawerHeaderNode && createPortal(<DraftAutosaveIndicator saveState={saveState} />, drawerHeaderNode)}
      <Stack direction={"row"} sx={{ alignItems: "center" }} spacing={1}>
        {previewMode ? (
          <>
            <Button startIcon={<ArrowBackRounded />} onClick={() => setPreviewSrc("")}>
              Zurück zum Editor
            </Button>
            <Box sx={{ flexGrow: 1 }} />
            <IconButton component={"a"} href={previewSrc} download={(payload.filename || "Dokument") + ".pdf"}>
              <DownloadRounded />
            </IconButton>
          </>
        ) : (
          <>
            <Chip size={"small"} label={"Entwurf"} />
            <Box sx={{ flexGrow: 1 }} />
            <ButtonLoading variant={"outlined"} fullWidth={false} onClick={openPreview} isLoading={previewIsLoading}>
              PDF-Vorschau
            </ButtonLoading>
          </>
        )}
      </Stack>
      {previewMode ? (
        <Box sx={documentEditorPanelStyles.previewBackground}>
          <Box sx={documentEditorPanelStyles.previewEmbedContainer}>
            <embed src={previewSrc} type="application/pdf" width="100%" height="100%" />
          </Box>
        </Box>
      ) : (
        <LegalbirdTipTapEditor editorSx={documentEditorPanelStyles.editor} />
      )}
    </Stack>
  );
}
